import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ConfigService } from '../config.service';
import {
  CreateVmConnectionTemplateRequest,
  RenameVmConnectionTemplateRequest,
  VmConnectionDuplicateCheckRequest,
  VmConnectionDuplicateCheckResponse,
  VmConnectionTemplateDetail,
  VmConnectionTemplateSummary,
} from '../../interfaces/vm-connection-templates/vm-connection-template.interface';

/**
 * Angular service for the per-class VM Connection Template feature. Wraps the four backend
 * endpoints under `/api/v1/vm-connection-templates`. Mirrors the design of
 * `PolicyTemplateService` for the policy template aggregate so the two read identically.
 */
@Injectable({ providedIn: 'root' })
export class VmConnectionTemplateService {
  private readonly baseUrl: string;

  constructor(
    private readonly http: HttpClient,
    private readonly configService: ConfigService,
  ) {
    this.baseUrl = `${this.configService.getApiUrl()}/v1/vm-connection-templates`;
  }

  /** List all VM connection templates (compact summary projection). */
  getAll(): Observable<VmConnectionTemplateSummary[]> {
    return this.http.get<VmConnectionTemplateSummary[]>(this.baseUrl);
  }

  /** Get full detail for a single VM connection template. */
  getById(id: string): Observable<VmConnectionTemplateDetail> {
    return this.http.get<VmConnectionTemplateDetail>(`${this.baseUrl}/${encodeURIComponent(id)}`);
  }

  /**
   * Pre-flight duplicate check. Computes the same content hash that the backend uses and
   * returns the existing template summary if one already exists. Used by the create wizard
   * to render a friendly "duplicate detected" message before the create POST is sent.
   */
  checkDuplicate(
    request: VmConnectionDuplicateCheckRequest,
  ): Observable<VmConnectionDuplicateCheckResponse> {
    return this.http.post<VmConnectionDuplicateCheckResponse>(
      `${this.baseUrl}/check-duplicate`, request,
    );
  }

  /** Create a new VM connection template. Returns the full detail DTO on success. */
  create(request: CreateVmConnectionTemplateRequest): Observable<VmConnectionTemplateDetail> {
    return this.http.post<VmConnectionTemplateDetail>(this.baseUrl, request);
  }

  /**
   * Renames a VM connection template (mutates only `templateName` + `description`). The
   * behavioural fields and `contentHash` are preserved server-side, so a rename never invalidates
   * existing attachments. Mirrors `PolicyTemplateService.rename`.
   */
  rename(id: string, request: RenameVmConnectionTemplateRequest): Observable<VmConnectionTemplateDetail> {
    return this.http.patch<VmConnectionTemplateDetail>(
      `${this.baseUrl}/${encodeURIComponent(id)}/rename`, request,
    );
  }

  /** Delete a VM connection template. Completes with no value on success (204 No Content). */
  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${encodeURIComponent(id)}`);
  }

  /**
   * Archives a VM connection template (soft-delete). No server-side hard block —
   * the frontend shows a warning when linked classes exist but allows the admin
   * to proceed.
   */
  archive(id: string): Observable<void> {
    return this.http.patch<void>(
      `${this.baseUrl}/${encodeURIComponent(id)}/archive`,
      null
    );
  }
}
